import { useMemo, useRef, type MutableRefObject } from 'react';
import { useSelector } from 'react-redux';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { useSearchParams } from '@bridebook/web/lib/search/hooks/query/use-search';
import { useDerivedSearchParams } from '@bridebook/web/lib/search/hooks/query/use-derived-search-params';
import { getProfile } from '@bridebook/web/lib/weddings/selectors';
import type { Couple, RequestContext } from '@bridebook/web/lib/ai-assistant/schemas';
import { getSearchLocation } from '../../search/selectors';

/**
 * Context fed into the AI assistant prompt on the server: search params
 * (countryCode + area) and the couple profile (guests/l10n + derived
 * rerank fields). The transport ships it under `requestContext` in the
 * `POST /api/ai-assistant` body; the server uses it to assemble the system
 * prompt and as defaults for the search tool.
 */
export type AIPromptContext = {
  requestContext: RequestContext;
};

export type UseAIPromptContextResult = {
  /**
   * Always-current prompt context. Held in a ref so the transport — which
   * is created once — can read the latest value on every `sendMessage`
   * without making the consumer re-render when upstream selectors change
   * (search params, wedding profile, derived rerank fields).
   */
  promptContextRef: MutableRefObject<AIPromptContext | null>;
  /** True once the page state has resolved enough to assemble the prompt context. */
  promptContextReady: boolean;
};

/**
 * Collects following kinds of inputs for the AI assistant prompt context:
 * - `searchParams` (countryCode + area) used in the system prompt
 *   and as defaults the search tool falls back to. Server resolves bounds /
 *   searchType / county from these via `processParameters`.
 * - `couple`: redux profile fields (guests/l10n) for prompt soft hints
 *   plus `getDerivedParams` rerank fields (weddingId/interactions/…) so chat
 *   ranking matches the "view all results" page.
 * - `locale` (market.locale which is users interface language) used in summary bar system prompt to write in the correct language.
 */
export const useAIPromptContext = ({
  isSearchPageOpened,
}: {
  isSearchPageOpened: boolean;
}): UseAIPromptContextResult => {
  const pageSearchParams = useSearchParams();
  const weddingProfile = useSelector(getProfile);
  const { locale } = useMarket();

  const searchLocation = useSelector(getSearchLocation);

  const { data: derived } = useDerivedSearchParams(
    isSearchPageOpened && pageSearchParams ? pageSearchParams : undefined,
  );

  const promptContext = useMemo<AIPromptContext | null>(() => {
    if (!pageSearchParams || !derived) return null;
    const couple: Couple = {
      ...(weddingProfile && {
        guests: weddingProfile.guests,
        l10n: weddingProfile.l10n,
      }),
      ...derived,
    };
    return {
      requestContext: {
        searchParams: {
          countryCode: pageSearchParams.market.country,
          area: searchLocation.selected.area,
        },
        couple: Object.keys(couple).length ? couple : undefined,
        locale,
      },
    };
  }, [pageSearchParams, weddingProfile, derived, searchLocation.selected.area, locale]);

  const promptContextRef = useRef<AIPromptContext | null>(promptContext);
  promptContextRef.current = promptContext;

  return {
    promptContextRef,
    promptContextReady: !!pageSearchParams,
  };
};
